import { formatVersionDisplay } from "../../../utils/semver";

export interface LegalDocumentPreviewProps {
  title: string;
  version: string | number;
  effectiveFrom?: string | null;
  updatedAt?: string | null;
  content: string;
}

const formatDate = (value?: string | null): string | null => {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const stripHtml = (html: string): string =>
  (html || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

const LegalDocumentPreview = ({
  title,
  version,
  effectiveFrom,
  updatedAt,
  content,
}: LegalDocumentPreviewProps) => {
  const hasContent = stripHtml(content).length > 0;
  const effectiveDate = formatDate(effectiveFrom);
  const lastUpdated = formatDate(updatedAt);

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Header block */}
        <div className="bg-neutral-50 border-b border-gray-200 px-8 py-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-primary-500 flex items-center justify-center shadow-md shadow-primary-500/20 shrink-0">
              <span className="font-bold text-white text-xl">D</span>
            </div>
            <div>
              <p className="text-lg font-bold text-gray-900 leading-snug">DiagnomIQ</p>
              <p className="text-sm font-medium text-primary-700 mt-0.5">Legal Document</p>
            </div>
          </div>
        </div>

        <div className="px-8 py-6 space-y-6">
          {/* Title and metadata */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {title || "Untitled document"}
            </h2>
            <div className="mt-1.5 h-1 w-full bg-primary-600 rounded-full" />

            <dl className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <dt className="text-xs text-gray-400">Version</dt>
                <dd className="text-sm font-medium text-gray-800">
                  {formatVersionDisplay(version, true)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-400">Effective date</dt>
                <dd className="text-sm font-medium text-gray-800">
                  {effectiveDate || "Not set"}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-400">Last updated</dt>
                <dd className="text-sm font-medium text-gray-800">
                  {lastUpdated || "Not saved yet"}
                </dd>
              </div>
            </dl>
          </div>

          {/* Formatted legal content */}
          {hasContent ? (
            <div
              className="text-[15px] leading-relaxed text-gray-800 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-1 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-3 [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_a]:text-primary-600 [&_a]:underline [&_strong]:font-semibold [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:text-gray-600"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          ) : (
            <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50/60 px-4 py-10 text-center text-sm text-gray-400">
              This document has no content yet. Switch to Edit to add content.
            </p>
          )}
        </div>

        {/* Footer note */}
        <div className="border-t border-gray-200 px-8 py-4">
          <p className="text-xs text-gray-400">
            This is how the document will be presented to users for acceptance.
          </p>
        </div>
      </div>

      <p className="text-xs text-gray-400 mt-3 text-center">
        Preview — reflects the current draft. Layout and branding only.
      </p>
    </div>
  );
};

export default LegalDocumentPreview;